/**
 * The legacy event-attendee `status` (#887 P5): one vocabulary for every surface that shows or sets it.
 *
 * Since #887 the server maintains `status` from the row's RSVP and attendance, and it can READ as six
 * values. Only the first four can be WRITTEN (`PUT /api/events/{id}/attendees/{attendee_id}` and the
 * create body accept `registered | confirmed | attended | no_show`); `declined` and `cancelled` come from
 * the RSVP and are display-only here.
 *
 * A status `<select>` offers the writable four. A row whose status is read-only gets one extra DISABLED
 * option for its own value (`statusOptions`), so a declined row reads "Declined" and never falls back to
 * the select's first option, "Registered".
 */

/** The statuses a request may set, in the order the selects offer them. */
export const LEGACY_STATUSES = ['registered', 'confirmed', 'attended', 'no_show'];

/** Label and colour classes for every status the API can return. */
export const STATUS_META = {
    registered: { label: 'Registered', bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-200' },
    confirmed: { label: 'Confirmed', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    attended: { label: 'Attended', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    no_show: { label: 'No Show', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    declined: { label: 'Declined', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' },
    cancelled: { label: 'Cancelled', bg: 'bg-stone-50', text: 'text-stone-500', border: 'border-stone-300' },
};

// A value the API should never send still renders as itself, in neutral colours.
const UNKNOWN_META = { bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-200' };

/** The label and colours for `status`; an unrecognised value is labelled with itself. */
export function statusMeta(status) {
    return STATUS_META[status] || { ...UNKNOWN_META, label: String(status ?? '') };
}

/**
 * The options for a row whose status is `current`: the writable statuses, preceded by `current` itself as
 * a disabled option when it cannot be written (`declined`, `cancelled`, or anything unrecognised).
 */
export function statusOptions(current) {
    const writable = LEGACY_STATUSES.map((value) => ({ value, label: STATUS_META[value].label, disabled: false }));
    if (!current || LEGACY_STATUSES.includes(current)) return writable;
    return [{ value: current, label: statusMeta(current).label, disabled: true }, ...writable];
}
